import { Button, Stack, Typography, useTheme } from "@mui/material";
import { MessageCircleIcon } from "lucide-react";
import type { FC } from "react";
import { NewContractOptionalFields } from "web_myplann/Goals/Components/PinContract/NewContractOptionalFields";
import type { NewContractFormOutput } from "web_myplann/Goals/Forms/newContractFormSchema";
import { useNewContractForm } from "web_myplann/Goals/Hooks/useNewContractForm";
import { useNewContractTypeOptions } from "web_myplann/Goals/Hooks/useNewContractTypeOptions";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { CtaCard } from "web_myplann/Shared/Components/Redesign/CtaCard";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";

const PageResources = Resources.Goals.Detail.Solution.Pin.New;

type Props = {
	open: boolean;
	isPending: boolean;
	onSubmit: (values: NewContractFormOutput) => void;
	onMessageAdvisor: () => void;
};

export const NewContractForm: FC<Props> = ({
	open,
	isPending,
	onSubmit,
	onMessageAdvisor,
}) => {
	const { palette, spacingTokens, iconSize } = useTheme();
	const { t } = useResource();
	const { form, type, nameHint, missingMessage, canSubmit } =
		useNewContractForm(open);
	const typeOptions = useNewContractTypeOptions();
	const { control, handleSubmit } = form;

	return (
		<Stack
			component="form"
			onSubmit={handleSubmit(onSubmit)}
			pb={spacingTokens.lg}
		>
			<Stack bgcolor={palette.background.paper}>
				<FlatRowFormSelect
					control={control}
					name="type"
					label={t(PageResources.typeLabel)}
					options={typeOptions}
					drawerTitle={t(PageResources.typeLabel)}
				/>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(PageResources.nameLabel)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					maxLength={ValidationLimits.TextLength.LongName}
				/>
				<FlatRowFormInput
					control={control}
					name="value"
					type="number"
					label={t(PageResources.valueLabel)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					suffix={t(Resources.Goals.Amount.currencySign)}
					max={ValidationLimits.Amount.Max}
					isLast
				/>
				<NewContractOptionalFields control={control} type={type} />
			</Stack>
			<Typography
				variant="hint"
				display="block"
				px={spacingTokens.lg}
				pt={spacingTokens.sm}
			>
				{nameHint}
			</Typography>
			<Stack px={spacingTokens.lg} pt={spacingTokens.xl} gap={spacingTokens.sm}>
				{missingMessage && (
					<Typography variant="hint" role="status" color={palette.grey[500]}>
						{missingMessage}
					</Typography>
				)}
				<Button
					type="submit"
					variant="primary"
					disabled={!canSubmit}
					loading={isPending}
				>
					{t(PageResources.submit)}
				</Button>
			</Stack>
			<Stack px={spacingTokens.lg} pt={spacingTokens.lg}>
				<CtaCard
					iconBg={palette.blue[100]}
					icon={
						<MessageCircleIcon size={iconSize.md} color={palette.blue[600]} />
					}
					title={t(PageResources.Help.advisorTitle)}
					description={t(PageResources.Help.advisorDescription)}
					onClick={onMessageAdvisor}
				/>
			</Stack>
		</Stack>
	);
};
